import { Layout } from "antd";
import { type CSSProperties, Suspense, useMemo, useEffect } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { CircleLoading } from "@/components/loading";
import { useSettings } from "@/store/settingStore";
import { useUserActions } from "@/store/userStore";
import { cn } from "@/utils";

import Header from "./header";
import Main from "./main";
import Nav from "./nav";

import { down, useMediaQuery } from "@/hooks";
import { ThemeLayout } from "#/enum";
import { NAV_COLLAPSED_WIDTH, NAV_WIDTH } from "./config";

function DashboardLayout() {
	const { themeLayout } = useSettings();
	const navigate = useNavigate();

	const mobileOrTablet = useMediaQuery(down("md"));
	const { refreshCredits, setBillingCycleEnd } = useUserActions();

	useEffect(() => {
		let lastDataString = "";
		const { VITE_APP_URL } = import.meta.env;

		const pollPaymentStatus = async () => {
			try {
				const activeTxId = sessionStorage.getItem("active_transaction_id");
				if (!activeTxId) return;

				const timestamp = Date.now();
				const res = await fetch(`${VITE_APP_URL}/api/payments/stripe/payment-status/${activeTxId}?_t=${timestamp}`, {
					headers: {
						'Cache-Control': 'no-cache, no-store, must-revalidate',
						'Pragma': 'no-cache'
					},
					cache: 'no-store'
				});
				if (res.ok) {
					const data = await res.json();
					const currentDataString = JSON.stringify(data);
					
					if (currentDataString !== lastDataString) {
						if (data.is_complete || data.payment_status === "paid") {
							toast.success(`Your subscription renewed! ${data.credits_allocated || 100} new credits added!`);
							
							await refreshCredits();
							
							if (data.billing_cycle_end) {
								setBillingCycleEnd(data.billing_cycle_end);
							}
							
							// Stop polling this transaction once it's complete
							sessionStorage.removeItem("active_transaction_id");
						} else if (
							data.status === "failed" || 
							data.status === "cancelled" || 
							data.payment_status === "failed" || 
							data.payment_status === "unpaid" || 
							data.payment_status === "canceled"
						) {
							sessionStorage.removeItem("active_transaction_id");
							await refreshCredits();
						}
					}
					lastDataString = currentDataString;
				} else if (res.status === 404 || res.status === 400) {
					sessionStorage.removeItem("active_transaction_id");
				}
			} catch (e) {
				// ignore network errors for background polling
			}
		};

		const intervalId = setInterval(pollPaymentStatus, 3000);
		return () => clearInterval(intervalId);
	}, [refreshCredits, setBillingCycleEnd]);

	useEffect(() => {
		const pendingPaymentStr = localStorage.getItem('pending_payment');
		if (pendingPaymentStr) {
			try {
				const pendingPayment = JSON.parse(pendingPaymentStr);
				const isExpired = pendingPayment.timestamp && (Date.now() - pendingPayment.timestamp > 30 * 60 * 1000);
				localStorage.removeItem('pending_payment');

				// Only redirect if they aren't already on outcome pages and transaction isn't old
				if (!isExpired &&
					!window.location.href.includes('payment/success') &&
					!window.location.href.includes('payment/cancelled')) {
					navigate(`/payment/cancelled?plan=${pendingPayment.plan || ''}&price=${pendingPayment.price || ''}&credits=${pendingPayment.credits || ''}&billingCycle=${pendingPayment.billingCycle || ''}&email=${pendingPayment.email || ''}&bill_id=${pendingPayment.bill_id || ''}&tier_id=${pendingPayment.tier_id || ''}`);
				}
			} catch (e) {
				localStorage.removeItem('pending_payment');
				console.error("Error parsing pending payment", e);
			}
		}
	}, [navigate]);

	const layoutClassName = useMemo(() => {
		return cn("flex w-screen h-screen overflow-hidden", themeLayout === ThemeLayout.Horizontal ? "flex-col" : "flex-row");
	}, [themeLayout]);

	const secondLayoutStyle: CSSProperties = {
		display: "flex",
		flexDirection: "column",
		width:"100%",
		transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
		paddingLeft: mobileOrTablet
			? 0
			: themeLayout === ThemeLayout.Horizontal
				? 0
				: themeLayout === ThemeLayout.Mini
					? NAV_COLLAPSED_WIDTH
					: NAV_WIDTH,
	};

	return (
		<Layout className={layoutClassName}>
			<Suspense fallback={<CircleLoading />}>
				<Layout style={secondLayoutStyle}>
					<Header />
					<Nav />
					<Main />
				</Layout>
			</Suspense>
		</Layout>
	);
}
export default DashboardLayout;
